import { useCallback, useEffect, useState } from "react";
import { applyTheme, storedTheme, type ThemeChoice } from "./theme";

/**
 * Preferences, modelled on what Outlook actually offers.
 *
 * The set is deliberately drawn from settings people are known to change —
 * reading pane position, mark-as-read timing, undo send, automatic replies —
 * rather than every switch that could exist. Each one here changes behaviour
 * somewhere; nothing is stored that nothing reads.
 *
 * Client preferences live in localStorage. Automatic replies do NOT: they are a
 * property of the mailbox, must apply when this browser is closed, and are
 * stored on the server as a JMAP VacationResponse.
 */

export type ReadingPane = "right" | "bottom" | "hidden";
export type Density = "comfortable" | "compact";
export type MarkRead = "immediately" | "delay" | "manual";
export type AfterAction = "next" | "list";
export type ReplyDefault = "reply" | "replyAll";

export interface Settings {
  theme: ThemeChoice;

  // Layout
  readingPane: ReadingPane;
  density: Density;
  /** Lines of body preview in the list. Outlook offers off / 1 / 2 / 3. */
  previewLines: 0 | 1 | 2 | 3;
  senderImages: boolean;

  // Messages
  conversationView: boolean;
  /** Outlook's "Newest message on top". */
  newestOnTop: boolean;
  markRead: MarkRead;
  /** Seconds, when markRead is "delay". Outlook's default is 5. */
  markReadSeconds: number;
  afterMove: AfterAction;
  emptyTrashOnSignOut: boolean;

  // Compose
  /** Seconds to hold a message before it actually goes. Outlook allows 0-10. */
  undoSendSeconds: number;
  replyDefault: ReplyDefault;
  alwaysShowCc: boolean;
  signatureOnReplies: boolean;

  // Reading
  alwaysShowImages: boolean;

  // Time
  hour24: boolean;
  /** 0 = Sunday, 1 = Monday. Drives the calendar grid. */
  weekStart: 0 | 1;
}

export const DEFAULTS: Settings = {
  theme: storedTheme(),
  readingPane: "right",
  density: "comfortable",
  previewLines: 1,
  senderImages: true,
  conversationView: true,
  newestOnTop: false,
  // Outlook's default is "when selection changes", i.e. as soon as you open it.
  markRead: "immediately",
  markReadSeconds: 5,
  afterMove: "list",
  emptyTrashOnSignOut: false,
  // Outlook defaults undo send ON. It is the single most regretted action in
  // mail, and a few seconds costs nothing.
  undoSendSeconds: 5,
  replyDefault: "reply",
  alwaysShowCc: false,
  signatureOnReplies: true,
  alwaysShowImages: false,
  hour24: false,
  weekStart: 1,
};

const KEY = "syndeo.mail.settings";

export function loadSettings(): Settings {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { ...DEFAULTS, theme: storedTheme() };
    // Merge over defaults so a setting added later is not undefined for anyone
    // who saved preferences before it existed.
    return { ...DEFAULTS, ...(JSON.parse(raw) as Partial<Settings>), theme: storedTheme() };
  } catch {
    return { ...DEFAULTS, theme: storedTheme() };
  }
}

function persist(settings: Settings): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(settings));
  } catch {
    /* private browsing — preferences just will not survive a reload */
  }
}

/** Settings plus a setter that persists and applies as a side effect. */
export function useSettings(): [Settings, <K extends keyof Settings>(key: K, value: Settings[K]) => void] {
  const [settings, setSettings] = useState<Settings>(loadSettings);

  const update = useCallback(<K extends keyof Settings>(key: K, value: Settings[K]) => {
    setSettings((prev) => {
      const next = { ...prev, [key]: value };
      persist(next);
      return next;
    });
  }, []);

  // The theme has its own storage key because it must apply before React mounts.
  useEffect(() => {
    applyTheme(settings.theme);
  }, [settings.theme]);

  return [settings, update];
}

/** Formats a time under the 12/24-hour preference. */
export function formatTime(date: Date, hour24: boolean): string {
  return date.toLocaleTimeString(undefined, {
    hour: hour24 ? "2-digit" : "numeric",
    minute: "2-digit",
    hour12: !hour24,
  });
}
